// Pure helpers for the phone-or-email login flow.
// Extracted from App.js so they can be unit-tested without rendering the
// whole app. App.js imports these instead of duplicating the logic.

// Permissive email check — backend re-validates and SendGrid is the
// ultimate source of truth for deliverability.
export const isValidEmail = (value) =>
  typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// Emails are matched case-insensitively in DDB, and stray whitespace from a
// paste should not create a "different" account.
export const normalizeEmail = (value) => (value ?? "").trim().toLowerCase();

// URL-encode the customer id for use in a REST path segment. Emails can
// contain "+" (e.g. "alice+test@example.com") which API Gateway decodes as
// a space if left unencoded, breaking the lookup.
export const buildCustomerIdPath = (customerId) =>
  encodeURIComponent(customerId ?? "");

// Normalizes a customer id into the value used to look the contact up in
// DDB. Phone users are stored in E.164, so a bare 10-digit number gets a
// "+1" prefix. Emails and already-prefixed phones are used verbatim --
// blindly prefixing "+1" to an email (the previous behaviour) produced
// "+1khubaib@example.com", which matched no record, so the avatar that
// gates on that lookup never loaded on the Account page.
export const toLookupId = (customerId) => {
  const id = (customerId ?? "").trim();
  if (!id) return "";
  if (id.includes("@") || id.startsWith("+")) return id;
  return `+1${id}`;
};

// A signup row exists in DDB after `/update-contact/{otp,email-otp}` is
// called, BEFORE the user has typed their first/last name in the
// "complete signup" screen. If the user closes the tab between OTP
// verify and profile completion, the row is left with no name -- a
// "ghost account". On the next login, this helper lets us detect that
// state and force the user back into the complete-signup flow instead
// of dropping them on the dashboard with an empty profile.
export const isProfileComplete = (dbUser) => {
  if (!dbUser || typeof dbUser !== "object") return false;
  const first = (dbUser.customer_firstname ?? "").toString().trim();
  const last = (dbUser.customer_lastname ?? "").toString().trim();
  // Either name field being populated is enough to treat the account as
  // real -- the user got past the signup form at some point. Requiring
  // both would lock out anyone with a mononym.
  return first.length > 0 || last.length > 0;
};
